// ============================================
// SISTEMA DE EXPORT/IMPORT PARA BACKUP
// ============================================

import { message } from "antd";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { unifiedStorage } from "../../../../../../../helpers";

// ============================================
// INTERFACES PARA EXPORT/IMPORT
// ============================================
const DB_NAME = "neron-exportador-unified-storage";

export interface BackupMetadata {
  version: string;
  timestamp: number;
  exportedBy: string;
  totalItems: number;
  categories: {
    photos: number;
    workflows: number;
    syncQueue: number;
    other: number;
  };
  totalSize: number;
  exportOptions: ExportOptions;
  checksums?: Record<string, string>;
}

export interface ExportOptions {
  includePhotos: boolean;
  includeWorkflows: boolean;
  includeSyncQueue: boolean;
  includeMetadata: boolean;
  compression: "none" | "low" | "medium" | "high";
  encryption?: {
    enabled: boolean;
    algorithm?: string;
  };
  dateRange?: {
    from?: Date;
    to?: Date;
  };
  maxFileSize?: number; // MB
}

export interface ImportResult {
  success: boolean;
  importedItems: {
    photos: number;
    workflows: number;
    syncQueue: number;
  };
  skippedItems: {
    duplicates: number;
    corrupted: number;
    oversized: number;
  };
  errors: string[];
  warnings: string[];
}

export interface BackupStats {
  totalBackups: number;
  lastBackup?: Date;
  totalSize: number;
  availableSpace?: number;
  recommendations: string[];
}

// ============================================
// CLASE PRINCIPAL DEL SISTEMA DE BACKUP
// ============================================

export class UnifiedBackupManager {
  private static instance: UnifiedBackupManager;
  private readonly BACKUP_VERSION = "1.0.0";
  private readonly MAX_FILE_SIZE_MB = 100; // 100MB por defecto
  private readonly CHUNK_SIZE = 1024 * 1024; // 1MB chunks para procesamiento

  static getInstance(): UnifiedBackupManager {
    if (!UnifiedBackupManager.instance) {
      UnifiedBackupManager.instance = new UnifiedBackupManager();
    }
    return UnifiedBackupManager.instance;
  }

  // ============================================
  // FUNCIONES DE EXPORT
  // ============================================

  async exportData(
    options: ExportOptions,
    onProgress?: (progress: number, status: string) => void
  ): Promise<void> {
    try {
      onProgress?.(0, "Inicializando export...");

      // Validar opciones
      this.validateExportOptions(options);

      // Crear ZIP
      const zip = new JSZip();
      const backupFolder = zip.folder("unified-storage-backup");
      if (!backupFolder) throw new Error("Error creando estructura de backup");

      let totalItems = 0;
      let processedItems = 0;
      const metadata: BackupMetadata = {
        version: this.BACKUP_VERSION,
        timestamp: Date.now(),
        exportedBy: "UnifiedStorageManager",
        totalItems: 0,
        categories: {
          photos: 0,
          workflows: 0,
          syncQueue: 0,
          other: 0,
        },
        totalSize: 0,
        exportOptions: options,
      };

      // Contar elementos totales para progreso
      if (options.includePhotos) {
        const photos = await this.getFilteredPhotos(options);
        totalItems += photos.length;
      }
      if (options.includeWorkflows) {
        const workflows = await this.getFilteredWorkflows(options);
        totalItems += workflows.length;
      }
      if (options.includeSyncQueue) {
        const syncItems = await this.getFilteredSyncQueue(options);
        totalItems += syncItems.length;
      }

      metadata.totalItems = totalItems;

      onProgress?.(80, `Exportando ${totalItems} elementos...`);

      // Exportar fotos
      if (options.includePhotos) {
        const photos = await this.getFilteredPhotos(options);
        const photosFolder = backupFolder.folder("photos");

        for (const photo of photos as any) {
          try {
            if (photo.blob) {
              // Verificar tamaño
              if (this.isFileSizeValid(photo.blob.size, options)) {
                // Determinar la extensión basada en el tipo MIME
                const mimeType = photo.blob.type || "image/jpeg";
                const extension = mimeType === "image/png" ? ".png" : ".jpg";
                await photosFolder?.file(`${photo.id}${extension}`, photo.blob);
                // Guardar metadatos en un archivo JSON separado si se desea
                if (options.includeMetadata) {
                  await photosFolder?.file(
                    `${photo.id}.json`,
                    JSON.stringify({
                      id: photo.id,
                      timestamp: photo.timestamp,
                      metadata: photo.metadata,
                      size: photo.blob.size,
                      type: mimeType,
                    })
                  );
                }
                metadata.categories.photos++;
                metadata.totalSize += photo.blob.size;
              }
            }

            processedItems++;
            onProgress?.(
              10 + (processedItems / totalItems) * 70,
              `Exportando fotos: ${processedItems}/${totalItems}`
            );
          } catch (error) {
            console.warn(`Error exportando foto ${photo.id}:`, error);
          }
        }
      }


      // Exportar workflows
      if (options.includeWorkflows) {
        const workflows = await this.getFilteredWorkflows(options);
        const workflowsFolder = backupFolder.folder("workflows");

        for (const workflow of workflows as any) {
          try {
            await workflowsFolder?.file(
              `${workflow.id}.json`,
              JSON.stringify(workflow)
            );
            metadata.categories.workflows++;
            metadata.totalSize += JSON.stringify(workflow).length;

            processedItems++;
            onProgress?.(
              10 + (processedItems / totalItems) * 70,
              `Exportando workflows: ${processedItems}/${totalItems}`
            );
          } catch (error) {
            console.warn(`Error exportando workflow ${workflow.id}:`, error);
          }
        }
      }

      // Exportar cola de sincronización
      if (options.includeSyncQueue) {
        const syncItems = await this.getFilteredSyncQueue(options);
        const syncFolder = backupFolder.folder("sync-queue");

        for (const item of syncItems as any) {
          try {
            await syncFolder?.file(`${item.id}.json`, JSON.stringify(item));
            metadata.categories.syncQueue++;
            metadata.totalSize += JSON.stringify(item).length;

            processedItems++;
            onProgress?.(
              10 + (processedItems / totalItems) * 70,
              `Exportando cola de sync: ${processedItems}/${totalItems}`
            );
          } catch (error) {
            console.warn(`Error exportando sync item ${item.id}:`, error);
          }
        }
      }

      // Agregar metadata
      if (options.includeMetadata) {
        await backupFolder.file(
          "metadata.json",
          JSON.stringify(metadata, null, 2)
        );
        await backupFolder.file(
          "stats.json",
          JSON.stringify(await unifiedStorage.getStorageStats(), null, 2)
        );
      }

      onProgress?.(80, "Comprimiendo archivo...");

      // Generar archivo con compresión
      const compressionLevel = this.getCompressionLevel(options.compression);
      const content = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: compressionLevel },
      });

      onProgress?.(95, "Guardando archivo...");

      // Guardar archivo
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const filename = `unified-storage-backup-${timestamp}.zip`;
      saveAs(content, filename);

      onProgress?.(100, "Export completado exitosamente");

      // Registrar backup en localStorage para historial
      this.registerBackup({
        filename,
        timestamp: Date.now(),
        size: content.size,
        metadata,
      });

      message.success(
        `Backup exportado: ${filename} (${this.formatBytes(content.size)})`
      );
    } catch (error) {
      console.error("Error durante export:", error);
      message.error("Error durante el export: " + (error as Error).message);
      throw error;
    }
  }

  // ============================================
  // FUNCIONES DE IMPORT
  // ============================================

  async importData(
    file: File,
    options: {
      overwriteExisting: boolean;
      validateIntegrity: boolean;
      skipLargeFiles: boolean;
    },
    onProgress?: (progress: number, status: string) => void
  ): Promise<ImportResult> {
    const result: ImportResult = {
      success: false,
      importedItems: { photos: 0, workflows: 0, syncQueue: 0 },
      skippedItems: { duplicates: 0, corrupted: 0, oversized: 0 },
      errors: [],
      warnings: [],
    };

    try {
      onProgress?.(0, "Leyendo archivo de backup...");

      // Leer ZIP
      const zip = await JSZip.loadAsync(file);
      const backupFolder = zip.folder("unified-storage-backup");

      if (!backupFolder) {
        throw new Error("Archivo de backup inválido: estructura no reconocida");
      }

      // Leer metadata
      const metadataFile = backupFolder.file("metadata.json");
      if (!metadataFile) {
        throw new Error("Archivo de backup corrupto: metadata no encontrado");
      }

      const metadataContent = await metadataFile.async("string");
      const metadata: BackupMetadata = JSON.parse(metadataContent);

      onProgress?.(10, "Validando backup...");

      // Validar versión
      if (!this.isVersionCompatible(metadata.version)) {
        result.warnings.push(
          `Versión de backup ${metadata.version} puede no ser completamente compatible`
        );
      }

      const totalItems = metadata.totalItems;
      let processedItems = 0;

      // Importar fotos
      if (metadata.categories.photos > 0) {
        onProgress?.(20, "Importando fotos...");
        const photosFolder = backupFolder.folder("photos");

        if (photosFolder) {
          const photoFiles = Object.keys(photosFolder.files).filter((name) =>
            name.endsWith(".json")
          );

          for (const photoFile of photoFiles) {
            try {
              const jsonContent = await photosFolder
                .file(photoFile)
                ?.async("string");
              const photoInfo = JSON.parse(jsonContent!);
              const blobFile = photosFolder.file(
                photoFile.replace(".json", ".blob")
              );

              if (blobFile) {
                const blobData = await blobFile.async("blob");

                // Verificar si ya existe
                if (!options.overwriteExisting) {
                  const existing = await unifiedStorage.getPhoto(photoInfo.id);
                  if (existing) {
                    result.skippedItems.duplicates++;
                    continue;
                  }
                }

                // Verificar tamaño
                if (
                  options.skipLargeFiles &&
                  blobData.size > this.MAX_FILE_SIZE_MB * 1024 * 1024
                ) {
                  result.skippedItems.oversized++;
                  continue;
                }

                await unifiedStorage.savePhoto(
                  photoInfo.id,
                  blobData,
                  photoInfo.metadata
                );
                result.importedItems.photos++;
              }

              processedItems++;
              onProgress?.(
                20 + (processedItems / totalItems) * 60,
                `Importando fotos: ${result.importedItems.photos}/${metadata.categories.photos}`
              );
            } catch (error) {
              result.errors.push(`Error importando foto: ${error}`);
              result.skippedItems.corrupted++;
            }
          }
        }
      }

      // Importar workflows
      if (metadata.categories.workflows > 0) {
        onProgress?.(60, "Importando workflows...");
        const workflowsFolder = backupFolder.folder("workflows");

        if (workflowsFolder) {
          const workflowFiles = Object.keys(workflowsFolder.files);

          for (const workflowFile of workflowFiles) {
            try {
              const jsonContent = await workflowsFolder
                .file(workflowFile)
                ?.async("string");
              const workflowInfo = JSON.parse(jsonContent!);

              // Verificar si ya existe
              if (!options.overwriteExisting) {
                const existing = await unifiedStorage.getWorkflowData(
                  workflowInfo.id
                );
                if (existing) {
                  result.skippedItems.duplicates++;
                  continue;
                }
              }

              await unifiedStorage.saveWorkflowData(
                workflowInfo.id,
                workflowInfo.data,
                workflowInfo.type
              );
              result.importedItems.workflows++;

              processedItems++;
              onProgress?.(
                60 + (processedItems / totalItems) * 60,
                `Importando workflows: ${result.importedItems.workflows}/${metadata.categories.workflows}`
              );
            } catch (error) {
              result.errors.push(`Error importando workflow: ${error}`);
              result.skippedItems.corrupted++;
            }
          }
        }
      }

      // Importar cola de sincronización
      if (metadata.categories.syncQueue > 0) {
        onProgress?.(80, "Importando cola de sincronización...");
        const syncFolder = backupFolder.folder("sync-queue");

        if (syncFolder) {
          const syncFiles = Object.keys(syncFolder.files);

          for (const syncFile of syncFiles) {
            try {
              const jsonContent = await syncFolder
                .file(syncFile)
                ?.async("string");
              const syncInfo = JSON.parse(jsonContent!);

              // Verificar si ya existe
              if (!options.overwriteExisting) {
                // Para sync queue, verificar por contenido similar
                const existing = await this.findSimilarSyncItem(syncInfo);
                if (existing) {
                  result.skippedItems.duplicates++;
                  continue;
                }
              }

              await this.importSyncQueueItem(syncInfo);
              result.importedItems.syncQueue++;

              processedItems++;
              onProgress?.(
                80 + (processedItems / totalItems) * 15,
                `Importando sync queue: ${result.importedItems.syncQueue}/${metadata.categories.syncQueue}`
              );
            } catch (error) {
              result.errors.push(`Error importando sync item: ${error}`);
              result.skippedItems.corrupted++;
            }
          }
        }
      }

      onProgress?.(95, "Finalizando import...");

      result.success = true;

      // Registrar import en historial
      this.registerImport({
        filename: file.name,
        timestamp: Date.now(),
        result,
        originalMetadata: metadata,
      });

      onProgress?.(100, "Import completado exitosamente");

      const totalImported = Object.values(result.importedItems).reduce(
        (sum, count) => sum + count,
        0
      );
      const totalSkipped = Object.values(result.skippedItems).reduce(
        (sum, count) => sum + count,
        0
      );

      message.success(
        `Import completado: ${totalImported} elementos importados, ${totalSkipped} omitidos`
      );
    } catch (error) {
      console.error("Error durante import:", error);
      result.errors.push(`Error general: ${(error as Error).message}`);
      message.error("Error durante el import: " + (error as Error).message);
    }

    return result;
  }

  // ============================================
  // FUNCIONES DE UTILIDAD Y HELPERS
  // ============================================

  private validateExportOptions(options: ExportOptions): void {
    if (
      !options.includePhotos &&
      !options.includeWorkflows &&
      !options.includeSyncQueue
    ) {
      throw new Error("Debes seleccionar al menos una categoría para exportar");
    }

    if (options.dateRange?.from && options.dateRange?.to) {
      if (options.dateRange.from > options.dateRange.to) {
        throw new Error(
          "La fecha de inicio no puede ser posterior a la fecha de fin"
        );
      }
    }
  }

  private async getFilteredPhotos(options: ExportOptions) {
    const photos = await unifiedStorage.listPhotos();
    return this.applyDateFilter(photos, options.dateRange);
  }

  private async getFilteredWorkflows(options: ExportOptions) {
    const workflows = await unifiedStorage.listWorkflows();
    return this.applyDateFilter(workflows, options.dateRange);
  }

  private async getFilteredSyncQueue(options: ExportOptions) {
    const syncItems = await unifiedStorage.listSyncQueue();
    return this.applyDateFilter(syncItems, options.dateRange);
  }

  private applyDateFilter<T extends { timestamp: number }>(
    items: T[],
    dateRange?: { from?: Date; to?: Date }
  ): T[] {
    if (!dateRange?.from && !dateRange?.to) return items;

    return items.filter((item) => {
      const itemDate = new Date(item.timestamp);
      if (dateRange.from && itemDate < dateRange.from) return false;
      if (dateRange.to && itemDate > dateRange.to) return false;
      return true;
    });
  }

  private isFileSizeValid(size: number, options: ExportOptions): boolean {
    const maxSize =
      (options.maxFileSize || this.MAX_FILE_SIZE_MB) * 1024 * 1024;
    return size <= maxSize;
  }

  private getCompressionLevel(
    compression: ExportOptions["compression"]
  ): number {
    switch (compression) {
      case "none":
        return 0;
      case "low":
        return 3;
      case "medium":
        return 6;
      case "high":
        return 9;
      default:
        return 6;
    }
  }

  private isVersionCompatible(version: string): boolean {
    // Implementar lógica de compatibilidad de versiones
    const [major] = version.split(".").map(Number);
    const [currentMajor] = this.BACKUP_VERSION.split(".").map(Number);
    return major === currentMajor;
  }

  private async findSimilarSyncItem(syncInfo: any): Promise<boolean> {
    // Implementar lógica para encontrar elementos similares en la cola
    const existing = await unifiedStorage.listSyncQueue();
    return existing.some(
      (item: any) =>
        item.url === syncInfo.url &&
        item.method === syncInfo.method &&
        JSON.stringify(item.body) === JSON.stringify(syncInfo.body)
    );
  }

  private async importSyncQueueItem(syncInfo: any): Promise<void> {
    // Implementar importación de elemento de cola de sincronización
    // Esto requiere acceso directo a la base de datos
    const dbName = DB_NAME;
    const storeName = "sync-queue";

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName);

      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction([storeName], "readwrite");
        const store = transaction.objectStore(storeName);

        const putRequest = store.put(syncInfo);
        putRequest.onsuccess = () => {
          db.close();
          resolve();
        };
        putRequest.onerror = () => {
          db.close();
          reject(putRequest.error);
        };
      };

      request.onerror = () => reject(request.error);
    });
  }

  private registerBackup(backup: any): void {
    try {
      const key = "unified-storage-backup-history";
      const history = JSON.parse(localStorage.getItem(key) || "[]");
      history.unshift(backup);

      // Mantener solo los últimos 10 backups en el historial
      if (history.length > 10) history.splice(10);

      localStorage.setItem(key, JSON.stringify(history));
    } catch (error) {
      console.warn("Error registrando backup en historial:", error);
    }
  }

  private registerImport(importInfo: any): void {
    try {
      const key = "unified-storage-import-history";
      const history = JSON.parse(localStorage.getItem(key) || "[]");
      history.unshift(importInfo);

      // Mantener solo los últimos 10 imports en el historial
      if (history.length > 10) history.splice(10);

      localStorage.setItem(key, JSON.stringify(history));
    } catch (error) {
      console.warn("Error registrando import en historial:", error);
    }
  }

  // ============================================
  // FUNCIONES DE UTILIDAD PÚBLICA
  // ============================================

  formatBytes(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  async getBackupStats(): Promise<BackupStats> {
    try {
      const backupHistory = JSON.parse(
        localStorage.getItem("unified-storage-backup-history") || "[]"
      );

      const totalSize = backupHistory.reduce(
        (sum: number, backup: any) => sum + (backup.size || 0),
        0
      );

      const lastBackup =
        backupHistory.length > 0
          ? new Date(backupHistory[0].timestamp)
          : undefined;

      // Obtener espacio disponible si es posible
      let availableSpace: number | undefined;
      if (navigator.storage?.estimate) {
        const estimate = await navigator.storage.estimate();
        availableSpace = estimate.quota
          ? estimate.quota - (estimate.usage || 0)
          : undefined;
      }

      // Generar recomendaciones
      const recommendations: string[] = [];
      if (backupHistory.length === 0) {
        recommendations.push("Crear tu primer backup para proteger tus datos");
      } else {
        const daysSinceLastBackup = lastBackup
          ? Math.floor(
              (Date.now() - lastBackup.getTime()) / (1000 * 60 * 60 * 24)
            )
          : 0;

        if (daysSinceLastBackup > 7) {
          recommendations.push(
            "Considera crear un nuevo backup (último hace más de 7 días)"
          );
        }

        if (availableSpace && availableSpace < 100 * 1024 * 1024) {
          // < 100MB
          recommendations.push(
            "Espacio de almacenamiento limitado, considera limpiar datos antiguos"
          );
        }
      }

      return {
        totalBackups: backupHistory.length,
        lastBackup,
        totalSize,
        availableSpace,
        recommendations,
      };
    } catch (error) {
      console.error("Error obteniendo estadísticas de backup:", error);
      return {
        totalBackups: 0,
        totalSize: 0,
        recommendations: ["Error obteniendo estadísticas de backup"],
      };
    }
  }

  getBackupHistory(): any[] {
    try {
      return JSON.parse(
        localStorage.getItem("unified-storage-backup-history") || "[]"
      );
    } catch (error) {
      console.error("Error obteniendo historial de backup:", error);
      return [];
    }
  }

  getImportHistory(): any[] {
    try {
      return JSON.parse(
        localStorage.getItem("unified-storage-import-history") || "[]"
      );
    } catch (error) {
      console.error("Error obteniendo historial de import:", error);
      return [];
    }
  }

  clearBackupHistory(): void {
    localStorage.removeItem("unified-storage-backup-history");
    localStorage.removeItem("unified-storage-import-history");
  }
}

// Instancia singleton
export const backupManager = UnifiedBackupManager.getInstance();
